import { router, useLocalSearchParams } from 'expo-router';
import { useEffect, useState } from 'react';
import { StyleSheet } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useCreateNpoPaymentAccountSteps } from '@/api/hooks/paymentAccountsHooks';
import { NonProfitOrg } from '@/api/models/nonProfitOrgModels';
import { Company, NpoPaymentAccountStepsResponse } from '@/api/models/paymentAccountsModels';
import FormWrapper from '@/components/forms/FormWrapper';
import GeneralForm from '@/components/forms/paymentNpoOnboarding/GeneralForm';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { useNpoMerchantOnboardingState } from '@/contexts/npoMerchantOnboardingContext';
import {
  CompanyOwnershipTypeEnum,
  GeneralData,
  OrganizationCategoryTypeEnum
} from '@/lib/validations/NpoPaymentOnboarding';
import { addressFromString, validateAddress } from '@/utils/addressFromString';
import { removeProtocolFromUrl } from '@/utils/cleanUrlProtocol';
import { formatSoftDescriptor } from '@/utils/formatSoftDescriptor';
import { getPrimaryZipCode } from '@/utils/getPrimaryZipCode';
import { i18n } from '@/utils/i18n';
import { safeJsonParse } from '@/utils/jsonParse';
import { normalizePhone } from '@/utils/normalizePhone';

const General = () => {
  const { npoItem } = useLocalSearchParams<{
    npoItem: string;
  }>();

  const { state: merchantState, setNpoId, setInitialNpoMerchantData } = useNpoMerchantOnboardingState();
  const merchantStateResponse = safeJsonParse<NpoPaymentAccountStepsResponse>(
    merchantState.initialNpoMerchantData ?? ''
  );

  const { mutateAsync: saveStep, isPending } = useCreateNpoPaymentAccountSteps();

  const { showSuccess } = useEndFlowModal();

  const [npoData, setNpoData] = useState<NonProfitOrg | undefined>(undefined);

  useEffect(() => {
    if (npoItem) {
      const parsedNpo = safeJsonParse<NonProfitOrg>(npoItem);
      if (parsedNpo) setNpoData(parsedNpo);
    }
  }, [npoItem]);

  const companyData = merchantStateResponse?.result?.companyData;

  const initialData: GeneralData = {
    companyLegalName: companyData?.company_legal_name ?? npoData?.name ?? '',
    companyDbaName: companyData?.company_dba_name ?? npoData?.name ?? '',
    companyOwnershipType:
      (companyData?.company_ownership_type as CompanyOwnershipTypeEnum) ?? CompanyOwnershipTypeEnum.NPO,
    companyFederalTaxId: companyData?.company_federal_tax_id ?? '',
    companyUrl: companyData?.company_url ?? '',
    organizationCategory:
      companyData?.company_mcc !== undefined
        ? (companyData?.company_mcc as OrganizationCategoryTypeEnum)
        : npoData?.type === 'CHARITIES'
          ? OrganizationCategoryTypeEnum.CHARITABLE
          : OrganizationCategoryTypeEnum.RELIGIOUS,
    customerSupportNumber: normalizePhone(companyData?.Customer_Support_Number) ?? '',
    companyPhoneNumber: normalizePhone(companyData?.company_phone_number) ?? '',
    legalAddress:
      validateAddress(companyData?.legal_address) ?? validateAddress(addressFromString(npoData?.address ?? '')) ?? '',
    dbaAddress:
      validateAddress(companyData?.dba_address) ?? validateAddress(addressFromString(npoData?.address ?? '')) ?? ''
  };

  const parseCompanyData = (data: GeneralData): Company => {
    const formattedLegalAddress = addressFromString(data?.legalAddress ?? '');
    const formattedDbaAddress = addressFromString(data?.dbaAddress ?? '');

    const parsedCompany: Company = {
      company_legal_name: data.companyLegalName,
      company_dba_name: data.companyDbaName,
      company_soft_descriptor: formatSoftDescriptor(data.companyLegalName),
      company_ownership_type: data.companyOwnershipType,
      company_federal_tax_id: data.companyFederalTaxId,
      company_url: removeProtocolFromUrl(data.companyUrl),
      company_mcc: data.organizationCategory ?? '5999',
      Customer_Support_Number: data.customerSupportNumber,
      company_phone_number: data.companyPhoneNumber,
      legal_address: {
        address: formattedLegalAddress?.address ?? '',
        postal_code: getPrimaryZipCode(formattedLegalAddress?.postal_code ?? ''),
        city: formattedLegalAddress?.city ?? '',
        state: formattedLegalAddress?.state ?? ''
      },
      dba_address: {
        address: formattedDbaAddress?.address ?? '',
        postal_code: getPrimaryZipCode(formattedDbaAddress?.postal_code ?? ''),
        city: formattedDbaAddress?.city ?? '',
        state: formattedDbaAddress?.state ?? ''
      }
    };

    return parsedCompany;
  };

  return (
    <SafeAreaView style={style.flexOne} edges={['bottom']}>
      <FormWrapper scrollEnable minHeightMinus={180}>
        <GeneralForm
          initialData={initialData}
          isLoading={isPending}
          onDone={(data) => {
            const parsedCompany = parseCompanyData(data);

            saveStep(
              {
                type: 'both',
                body: {
                  nonProfitOrgId: merchantState.npoId ?? npoData?.id ?? '',
                  company: parsedCompany
                }
              },
              {
                onSuccess: (data) => {
                  setNpoId(data.nonProfitOrgId);
                  setInitialNpoMerchantData(
                    JSON.stringify({
                      files: [],
                      result: data
                    })
                  );

                  router.navigate({
                    pathname: '/npoPaymentOnboarding/owner',
                    params: {
                      npoItem
                    }
                  });
                }
              }
            );
          }}
          onSaveAndContinue={(data) => {
            const parsedCompany = parseCompanyData(data);

            saveStep(
              {
                type: 'both',
                body: {
                  nonProfitOrgId: merchantState.npoId ?? npoData?.id ?? '',
                  company: parsedCompany
                }
              },
              {
                onSuccess: (data) => {
                  setNpoId(data.nonProfitOrgId);
                  setInitialNpoMerchantData(
                    JSON.stringify({
                      files: [],
                      result: data
                    })
                  );
                  showSuccess({
                    title: i18n.t('profile.saved_continue_title'),
                    message: i18n.t('profile.saved_continue_description'),
                    onButtonPress: () => {
                      router.dismissAll();
                      router.navigate('/(app)/(bottomTabs)/profile/npoProfile/insights');
                    }
                  });
                }
              }
            );
          }}
        />
      </FormWrapper>
    </SafeAreaView>
  );
};

export default General;

const style = StyleSheet.create({
  flexOne: {
    flex: 1
  }
});
